// Stone training: a hunt-only coin and stone sink (Cole 2026-10-20, from the
// reference game's stat training). Each step adds +1 to one base stat of one
// creature (HP, Attack, Defense, Magic or Resist; never Speed), like refine,
// but it counts on hunts, bosses and the World Boss only: the Arena, Halls,
// Guild War and tournaments never see it (shared/pvp.ts uses refine alone),
// so it can't buy PvP strength.
//
// Cost is cubic like refine: step N costs TRAIN_STONES × (3N² − 3N + 1)
// ascension stones of one of the creature's own elements, plus TRAIN_COINS ×
// the same multiplier in coins (burned). +1 = 25 stones and 100k coins,
// +3 = 675 stones and 2.7M in total, +10 = 25,000 stones and 100M.
//
// Why 25: stones piled up on the market (the 1,000-trainer sim, 2026-10-20:
// 10-71× more found than used, at the 2-coin floor within three weeks), and
// the reference charges 500 × N³ with drops about 4-8× ours, so 25 keeps
// its pace (+1 in a day or two of hard hunting, +3 in about two months).
// Training stays on the creature through ascension, stars and trades.
import { elementStone } from './items.ts';
import { SPECIES } from './species.ts';

export const TRAIN_STONES = 25;
export const TRAIN_COINS = 100_000;
/** Stat indices that can be trained (STAT_NAMES order): HP, Attack, Defense, Magic, Resist. */
export const TRAIN_STATS = [0, 1, 2, 3, 4] as const;
/** Most steps one message may buy at once. */
export const MAX_TRAIN_STEPS = 10;

const mult = (n: number) => 3 * n * n - 3 * n + 1;
/** Stones for step n (1-based) of one stat. */
export const trainStepStones = (n: number): number => TRAIN_STONES * mult(n);
/** Coins for step n (1-based) of one stat. */
export const trainStepCoins = (n: number): number => TRAIN_COINS * mult(n);
/** Stones to go from +from to +to in one stat. */
export const trainStonesBetween = (from: number, to: number): number => TRAIN_STONES * (to ** 3 - from ** 3);
/** Coins to go from +from to +to in one stat. */
export const trainCoinsBetween = (from: number, to: number): number => TRAIN_COINS * (to ** 3 - from ** 3);

/** The stones that can train this species: one per own element, first element first. */
export function trainStones(speciesId: string): string[] {
  return [...new Set(SPECIES[speciesId].elements.map(elementStone))];
}

/** A creature's training steps per stat (always 5 entries). */
export function trainOf(c: { train?: readonly number[] }): number[] {
  return TRAIN_STATS.map((i) => Math.max(0, Math.floor(c.train?.[i] ?? 0)));
}

export const trainTotal = (c: { train?: readonly number[] }): number => trainOf(c).reduce((a, b) => a + b, 0);

/** Stones already spent training this creature. */
export function trainSpent(c: { train?: readonly number[] }): number {
  return trainOf(c).reduce((a, n) => a + TRAIN_STONES * n ** 3, 0);
}

export const canTrainStat = (i: number): boolean => (TRAIN_STATS as readonly number[]).includes(i);
